import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import memberstackDOM from '@memberstack/dom';

// Types
interface Member {
  id: string;
  verified: boolean;
  profileImage: string;
  auth: {
    email: string;
    hasPassword: boolean;
    providers: { provider: string }[];
  };
  loginRedirect: string;
  stripeCustomerId: string;
  createdAt: string;
  metaData: object;
  customFields: object;
  permissions: string[];
  planConnections: any[];
  [key: string]: any;
}

interface AuthContextType {
  member: Member | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  memberstack: any;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, userData?: { firstName: string; lastName: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshMember: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
  publicKey: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, publicKey }) => {
  const [member, setMember] = useState<Member | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [memberstack, setMemberstack] = useState<any>(null);

  // Initialize Memberstack
  useEffect(() => {
    const initMemberstack = async () => {
      try {
        const ms = memberstackDOM.init({
          publicKey: publicKey,
          useCookies: true
        });
        setMemberstack(ms);
        
        // Check if user is already logged in
        const currentMember = await ms.getCurrentMember();
        // Only set member if we have actual member data, not just {data: null}
        if (currentMember && currentMember.data) {
          setMember(currentMember.data);
        } else {
          setMember(null);
        }
      } catch (error) {
        console.error('Failed to initialize Memberstack:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initMemberstack();
  }, [publicKey]);

  // Login function
  const login = async (email: string, password: string) => {
    if (!memberstack) return;
    
    try {
      await memberstack.loginMemberEmailPassword({
        email,
        password
      });
      // Redirect to onboarding after login
      window.location.href = '/';
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Signup function
  const signup = async (email: string, password: string, userData?: { firstName: string; lastName: string }) => {
    if (!memberstack) return;
    
    try {
      await memberstack.signupMemberEmailPassword({
        email,
        password,
        customFields: userData ? {
          firstName: userData.firstName,
          lastName: userData.lastName
        } : undefined
      });
      
      // Refresh member data after signup
      const currentMember = await memberstack.getCurrentMember();
      if (currentMember && currentMember.data) {
        setMember(currentMember.data);
      }
    } catch (error) {
      console.error('Signup failed:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    if (!memberstack) return;
    
    try {
      await memberstack.logout();
      setMember(null);
      // Redirect to onboarding after logout
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Refresh member data
  const refreshMember = async () => {
    if (!memberstack) return;
    
    try {
      const currentMember = await memberstack.getCurrentMember();
      // Only set member if we have actual member data, not just {data: null}
      if (currentMember && currentMember.data) {
        setMember(currentMember.data);
      } else {
        setMember(null);
      }
    } catch (error) {
      console.error('Failed to refresh member:', error);
      setMember(null);
    }
  };

  const value: AuthContextType = {
    member,
    isLoading,
    isAuthenticated: !!(member && member.auth && member.auth.email),
    memberstack,
    login,
    signup,
    logout,
    refreshMember,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
